/**
 * ============================================================================
 * CheckoutSuccessPage.js - Stripe Checkout Success Handler
 * ============================================================================
 * Displayed after successful Stripe checkout
 * Created: January 23, 2026 - Phase 3D Subscription Billing
 * Updated: January 27, 2026 - Improved UI design, 10-second countdown
 * Updated: January 29, 2026 - Expanded getting started instructions, removed countdown
 * Updated: May 2026 - Trial copy removed (Free + Starter rollout). Page is now
 *                     status-aware: shows 'Subscription Active' for paid signups,
 *                     'Trial Started' only when backend reports status='trial'
 *                     (Stripe-initiated trial via admin tool or future flows).
 *                     GA4 event renamed trial_started -> subscription_started.
 * 
 * FEATURES:
 * - Confirms successful payment
 * - Shows subscription details (status-aware: active vs trial)
 * - Professional modal design
 * - Getting started instructions for new users
 * - Manual navigation to dashboard (no auto-redirect)
 * - Handles session verification
 * ============================================================================
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');

// GA4 tracking helper - safe to call even if gtag isn't loaded
const trackEvent = (eventName, params = {}) => {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag('event', eventName, params);
  }
};

function CheckoutSuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  
  const [status, setStatus] = useState('verifying'); // 'verifying', 'success', 'error'
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [error, setError] = useState('');
  const subscriptionEventFired = useRef(false); // Prevent double-firing GA4 subscription_started event
  
  // GA4: Fire subscription_started conversion event once when status becomes success
  // (Renamed from trial_started May 2026 — trial removed in Free + Starter rollout.
  // If status comes back as 'trial' from a Stripe-initiated trial, we still fire
  // subscription_started since the conversion event is the same business signal.)
  useEffect(() => {
    if (status === 'success' && !subscriptionEventFired.current) {
      subscriptionEventFired.current = true;
      const tier = subscriptionDetails?.tier || subscriptionDetails?.subscription?.tier || 'unknown';
      trackEvent('subscription_started', {
        tier: tier,
        session_id: sessionId || 'none'
      });
      console.log('📊 GA4: subscription_started event fired');
    }
  }, [status, subscriptionDetails, sessionId]);
  
  // Verify the checkout session
  useEffect(() => {
    const verifySession = async () => {
      // Move pending tokens from sessionStorage to localStorage (registration flow)
      const pendingAccess = sessionStorage.getItem('pendingAccessToken');
      const pendingRefresh = sessionStorage.getItem('pendingRefreshToken');
      if (pendingAccess && pendingRefresh) {
        localStorage.setItem('accessToken', pendingAccess);
        localStorage.setItem('refreshToken', pendingRefresh);
        sessionStorage.removeItem('pendingAccessToken');
        sessionStorage.removeItem('pendingRefreshToken');
        sessionStorage.removeItem('pendingOrgId');
        sessionStorage.removeItem('checkoutOrgId');
        console.log('✅ Tokens activated after successful checkout');
      }
      
      if (!sessionId) {
        setStatus('success'); // No session to verify, just show success
        return;
      }
      
      try {
        const token = localStorage.getItem('accessToken');
        const response = await fetch(`${API_URL}/subscription/status`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          setSubscriptionDetails(data.subscription || data);
          setStatus('success');
        } else {
          // Even if verification fails, we show success since Stripe redirected here
          setStatus('success');
        }
      } catch (err) {
        console.error('Error verifying session:', err);
        setStatus('success'); // Still show success, Stripe handles the actual verification
      }
    };
    
    verifySession();
  }, [sessionId]);
  
  // Get tier display name
  const getTierDisplayName = (tier) => {
    const names = {
      'starter': 'Starter',
      'basic': 'Basic',
      'professional': 'Professional',
      'enterprise': 'Enterprise'
    };
    return names[tier?.toLowerCase()] || tier || 'Active';
  };
  
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
      padding: 24
    }}>
      <div style={{
        background: '#fff',
        borderRadius: 24,
        padding: 48,
        maxWidth: 560,
        width: '100%',
        textAlign: 'center',
        boxShadow: '0 25px 80px rgba(0,0,0,0.12), 0 10px 30px rgba(0,0,0,0.08)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Top accent bar */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 6,
          background: 'linear-gradient(90deg, #22c55e, #16a34a, #15803d)'
        }} />
        
        {status === 'verifying' ? (
          <>
            <div style={{
              width: 80,
              height: 80,
              margin: '0 auto 24px',
              border: '4px solid #e5e7eb',
              borderTopColor: '#22c55e',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
            <h1 style={{ fontSize: 24, fontWeight: 700, color: '#1f2937', marginBottom: 8 }}>
              Verifying Payment...
            </h1>
            <p style={{ color: '#6b7280', fontSize: 16 }}>
              Please wait while we confirm your subscription.
            </p>
          </>
        ) : status === 'success' ? (
          <>
            {/* Success Icon with Animation */}
            <div style={{
              width: 100,
              height: 100,
              margin: '0 auto 28px',
              background: 'linear-gradient(135deg, #22c55e, #16a34a)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 12px 40px rgba(34, 197, 94, 0.35)',
              animation: 'scaleIn 0.5s ease-out'
            }}>
              <svg width="50" height="50" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            
            {/* Title — status-aware. Most signups are 'active' immediately
                after Phase 3 rollout (May 2026). 'trial' branch retained for
                Stripe-initiated trials (admin tool, future flows). */}
            <h1 style={{ 
              fontSize: 32, 
              fontWeight: 800, 
              color: '#1f2937', 
              marginBottom: 12
            }}>
              {subscriptionDetails?.status === 'trial' ? 'Trial Started! 🎉' : 'Subscription Active! 🎉'}
            </h1>
            
            <p style={{ color: '#6b7280', fontSize: 17, marginBottom: 28, lineHeight: 1.5 }}>
              Welcome to <span style={{ color: '#2d6a4f', fontWeight: 600 }}>Tee Time Organizer</span>!<br />
              {subscriptionDetails?.status === 'trial'
                ? 'Your free trial is now active.'
                : 'Your subscription is now active.'}
            </p>
            
            {/* Subscription Details Card */}
            <div style={{
              background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)',
              border: '1px solid #bbf7d0',
              borderRadius: 16,
              padding: 24,
              marginBottom: 28
            }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                gap: 8,
                marginBottom: 16
              }}>
                <span style={{ fontSize: 24 }}>⛳</span>
                <span style={{ fontSize: 16, fontWeight: 700, color: '#166534' }}>
                  {subscriptionDetails?.status === 'trial' ? 'Free Trial Active' : 'Subscription Active'}
                </span>
              </div>
              
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: '1fr 1fr', 
                gap: 16,
                textAlign: 'left'
              }}>
                <div style={{
                  background: '#fff',
                  borderRadius: 10,
                  padding: 14,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                }}>
                  <div style={{ color: '#6b7280', fontSize: 12, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Plan</div>
                  <div style={{ fontWeight: 700, color: '#1f2937', fontSize: 18 }}>
                    {getTierDisplayName(subscriptionDetails?.tier || subscriptionDetails?.subscription?.tier)}
                  </div>
                </div>
                <div style={{
                  background: '#fff',
                  borderRadius: 10,
                  padding: 14,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                }}>
                  <div style={{ color: '#6b7280', fontSize: 12, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Status</div>
                  <div style={{ fontWeight: 700, color: '#22c55e', fontSize: 18, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{
                      width: 10,
                      height: 10,
                      background: '#22c55e',
                      borderRadius: '50%',
                      display: 'inline-block'
                    }} />
                    {subscriptionDetails?.status === 'trial' ? 'Trial' : 'Active'}
                  </div>
                </div>
              </div>
              
              {/* Cancel-anytime notice (status-aware copy) */}
              <div style={{
                marginTop: 16,
                padding: 12,
                background: '#fff',
                borderRadius: 8,
                border: '1px dashed #bbf7d0',
                textAlign: 'center'
              }}>
                {subscriptionDetails?.status === 'trial' ? (
                  <>
                    <div style={{ fontSize: 13, color: '#6b7280' }}>
                      💳 Your card will be charged when your trial ends
                    </div>
                    <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>
                      Cancel anytime from Settings → Subscription
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ fontSize: 13, color: '#6b7280' }}>
                      💳 Your first payment has been processed
                    </div>
                    <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>
                      Cancel anytime from Settings → Subscription
                    </div>
                  </>
                )}
              </div>
            </div>
            
            {/* Getting Started Section */}
            <div style={{
              background: '#f9fafb',
              borderRadius: 12,
              padding: 24,
              marginBottom: 28,
              textAlign: 'left'
            }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#374151', marginBottom: 8 }}>
                🚀 Getting Started
              </div>
              <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 16, lineHeight: 1.5 }}>
                You're about to be redirected to your new organizer dashboard. Here are the basic steps to get up and running:
              </p>
              <ol style={{
                margin: 0,
                padding: '0 0 0 20px',
                fontSize: 14,
                color: '#4b5563',
                lineHeight: 2
              }}>
                <li><strong>Add your course(s)</strong> – Include tee box details for handicap calculations</li>
                <li><strong>Add your players</strong> – Name and email address is all that's needed</li>
                <li><strong>Create your first tee time(s)</strong> – Then publish to notify your players</li>
                <li><strong>Send the welcome email</strong> – Gives players access to their Player Portal</li>
              </ol>
              <p style={{ fontSize: 13, color: '#6b7280', marginTop: 16, marginBottom: 0, lineHeight: 1.5 }}>
                These steps are outlined in the <strong style={{ color: '#2d6a4f' }}>Implementation Guide</strong> available from your dashboard. Help is also available on every screen throughout the app.
              </p>
            </div>
            
            {/* CTA Button */}
            <button
              onClick={() => navigate('/dashboard', { 
                state: { 
                  message: subscriptionDetails?.status === 'trial'
                    ? 'Welcome! Your free trial has started.'
                    : 'Welcome! Your subscription is now active.',
                  isNewSubscription: true,
                  isTrial: subscriptionDetails?.status === 'trial'
                },
                replace: true 
              })}
              style={{
                width: '100%',
                padding: '16px 32px',
                background: 'linear-gradient(135deg, #2d6a4f, #1b4332)',
                color: '#fff',
                border: 'none',
                borderRadius: 12,
                fontSize: 17,
                fontWeight: 700,
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
                boxShadow: '0 4px 16px rgba(45, 106, 79, 0.35)'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 8px 24px rgba(45, 106, 79, 0.45)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 16px rgba(45, 106, 79, 0.35)';
              }}
            >
              Go to Dashboard Now →
            </button>
          </>
        ) : (
          <>
            {/* Error state */}
            <div style={{
              width: 80,
              height: 80,
              margin: '0 auto 24px',
              background: '#fef2f2',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <span style={{ fontSize: 40 }}>⚠️</span>
            </div>
            
            <h1 style={{ fontSize: 24, fontWeight: 700, color: '#1f2937', marginBottom: 8 }}>
              Something Went Wrong
            </h1>
            
            <p style={{ color: '#6b7280', fontSize: 16, marginBottom: 24 }}>
              {error || 'We couldn\'t verify your payment. Don\'t worry - if you were charged, your subscription is still active.'}
            </p>
            
            <button
              onClick={() => navigate('/dashboard', { replace: true })}
              style={{
                padding: '14px 32px',
                background: '#2d6a4f',
                color: '#fff',
                border: 'none',
                borderRadius: 10,
                fontSize: 16,
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              Go to Dashboard
            </button>
          </>
        )}
      </div>
      
      {/* CSS Animations */}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @keyframes scaleIn {
          from { transform: scale(0); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
}

export default CheckoutSuccessPage;
